/**
 * ipinfo.io lookup (ISS-09-009). Unauthenticated requests (no API token) are
 * fine for low-volume admin usage, but share ipinfo.io's global anonymous
 * rate limit — if that's ever exhausted, lookups fail silently (null) rather
 * than breaking the page.
 */

export interface IpGeolocationResult {
    country?: string;
    region?: string;
    city?: string;
    org?: string;
}

const cache = new Map<string, Promise<IpGeolocationResult | null>>();

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function toOptionalString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

export class IpGeolocationApi {
    /** Cached per IP for the lifetime of the page — repeat hovers never refetch. */
    public static fetch(ip: string): Promise<IpGeolocationResult | null> {
        const trimmed = ip.trim();
        if (!trimmed || trimmed === "-") return Promise.resolve(null);

        const cached = cache.get(trimmed);
        if (cached) return cached;

        // eslint-disable-next-line no-restricted-syntax -- this -api.ts module is the single, centralized caller for this third-party (non-app) endpoint; ApiBase is for our own backend (CSRF/contract-validated) and doesn't fit a cross-origin GET to ipinfo.io.
        const promise = fetch(`https://ipinfo.io/${encodeURIComponent(trimmed)}/json`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data: unknown) => {
                if (!isRecord(data)) return null;

                return {
                    country: toOptionalString(data.country),
                    region: toOptionalString(data.region),
                    city: toOptionalString(data.city),
                    org: toOptionalString(data.org),
                };
            })
            .catch(() => null);

        cache.set(trimmed, promise);
        return promise;
    }
}
